import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { Button, Dialog, Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  ApiReportTemplateFileUploadConfigChoices,
  ExportTypeEnum,
  ReportType,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { ReportNode } from "@providers/ReportProvider";

interface ExportWithTemplateV2Props {
  reportData: ReportNode;
  refetchReport?: () => Promise<ReportType | undefined>;
}

const EXPORT_FORMAT_OPTIONS: SelectItem[] = [
  { id: ExportTypeEnum.Docx, primary: "Microsoft Word (.docx)" },
  { id: ExportTypeEnum.Pdf, primary: "PDF Document (.pdf)" },
];

const ExportWithTemplate: React.FC<ExportWithTemplateV2Props> = ({ reportData, refetchReport }) => {
  const isCatalightTemplate =
    reportData.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight;
  const isCatalightIa = isCatalightTemplate && reportData.reportType === ApiReportReportTypeChoices.InitialAssessment;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedExportType, setSelectedExportType] = useState<SelectItem>(EXPORT_FORMAT_OPTIONS[0]);

  const { handleExport, isExporting, isLoading, exportError, clearExportError } = useTemplateExport({
    reportData,
    exportType: isCatalightIa ? (selectedExportType.id as ExportTypeEnum) : ExportTypeEnum.Docx,
    refetchReport: isCatalightIa ? refetchReport : undefined,
    onExportComplete: undefined,
  });

  const handleDialogExport = async () => {
    const success = await handleExport();
    if (success) {
      setIsDialogOpen(false);
    }
  };

  const getExportButtonText = () => {
    if (!isExporting) return "Export";
    return selectedExportType.id === ExportTypeEnum.Pdf ? "Converting to PDF..." : "Exporting...";
  };

  return (
    <div className="flex flex-col items-end">
      <Button
        text={
          <span className="flex items-center gap-1.5">
            <ArrowUpTrayIcon className="h-4 w-4" />
            Export
          </span>
        }
        disabled={isLoading || isExporting}
        id="report-export-btn"
        appearance="text"
        onClick={() => {
          if (isCatalightIa) {
            setIsDialogOpen(true);
          } else {
            handleExport();
          }
        }}
      />

      <Dialog
        title="Export Report"
        open={isDialogOpen}
        showCloseButton={!isExporting}
        setOpen={(open) => {
          if (!isExporting) {
            setIsDialogOpen(open);
            if (!open) clearExportError();
          }
        }}
        primaryButton={{
          text: (
            <div className="flex gap-1 w-max items-center">
              {isExporting && <Loader className="w-4 h-4" />}
              {getExportButtonText()}
            </div>
          ),
          onClick: handleDialogExport,
          disabled: isExporting,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setIsDialogOpen(false);
            clearExportError();
          },
          disabled: isExporting,
        }}
      >
        <div className="flex flex-col gap-4 py-2">
          <div className="relative z-10">
            <Select
              title="Export format"
              items={EXPORT_FORMAT_OPTIONS}
              placeholderText="Select format"
              selected={selectedExportType}
              setSelected={setSelectedExportType}
              formNoValidate
            />
          </div>
          {exportError && <Small className="text-red-500">{exportError}</Small>}
        </div>
      </Dialog>
    </div>
  );
};

export default ExportWithTemplate;
